/**
 * Positions page - Enhanced decision-optimized table with sparklines and performance metrics.
 */

import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Search,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { portfolioApi, recommendationsApi } from '../services/api';
import type { Position, Recommendation } from '../types';
import { usePortfolio } from '../contexts/PortfolioContext';
import { SparklineWithPeriod } from '../components/SparklineWithPeriod';

type SortField = 'ticker' | 'value' | 'gain' | 'dayChange' | 'costBasis';

export function PositionsPage() {
  const { selectedPortfolioId, selectedPortfolio } = usePortfolio();
  const [positions, setPositions] = useState<Position[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('ticker');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [sparklinePeriod, setSparklinePeriod] = useState<'1H' | '12H' | '1D' | '7D' | '1M' | '1Y'>('1M');

  useEffect(() => {
    if (selectedPortfolioId) {
      loadData();
    }
  }, [selectedPortfolioId]);

  const loadData = async () => {
    if (!selectedPortfolioId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load positions first (critical for initial paint)
      const portfolioData = await portfolioApi.getCurrentById(selectedPortfolioId);
      setPositions(portfolioData.positions);
      setLoading(false); // Render immediately after positions load
      
      // Load recommendations asynchronously (non-blocking)
      recommendationsApi
        .getOpen(selectedPortfolioId)
        .then((recommendationsData) => {
          setRecommendations(recommendationsData.recommendations || []);
        })
        .catch(() => {
          setRecommendations([]);
        });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions');
      console.error('Positions load error:', err);
      setLoading(false);
    }
  };

  // Get recommendation for a ticker
  const getRecommendation = (ticker: string): Recommendation | undefined => {
    return recommendations.find((rec) => rec.ticker === ticker && rec.status === 'PENDING');
  };

  // Calculate cost basis price (average entry price)
  const getCostBasisPrice = (position: Position): number => {
    if (!position.quantity || position.quantity === 0) return 0;
    return position.costBasis / position.quantity;
  };

  // Calculate total gain in dollars
  const getTotalGainDollar = (position: Position): number => {
    return position.currentValue - position.costBasis;
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = positions.filter(
      (pos) =>
        pos.ticker.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pos.bucket.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'ticker':
          comparison = a.ticker.localeCompare(b.ticker);
          break;
        case 'value':
          comparison = a.currentValue - b.currentValue;
          break;
        case 'gain':
          comparison = a.gainPct - b.gainPct;
          break;
        case 'dayChange':
          const aDayChange = a.dayChange?.percent || 0;
          const bDayChange = b.dayChange?.percent || 0;
          comparison = aDayChange - bDayChange;
          break;
        case 'costBasis':
          comparison = a.costBasis - b.costBasis;
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [positions, searchTerm, sortBy, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'ticker' ? 'asc' : 'desc');
    }
  };

  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatDayChange = (position: Position): string => {
    const dollar = position.dayChange?.dollar;
    const percent = position.dayChange?.percent;
    
    if (dollar === null || dollar === undefined || percent === null || percent === undefined) {
      return '—';
    }
    
    const dollarStr = formatCurrency(dollar);
    const percentStr = formatPercent(percent);
    return `${dollarStr} (${percentStr})`;
  };

  const formatLastRunTime = (timestamp: string | null): string => {
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  const formatLastRunTimeFull = (timestamp: string | null): string => {
    if (!timestamp) return '—';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '—';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {selectedPortfolio && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedPortfolio.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedPortfolio.portfolioType} Portfolio
            </Typography>
          </CardContent>
        </Card>
      )}
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Portfolio Positions
          </Typography>
          <TextField
            fullWidth
            placeholder="Search by ticker or bucket..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Chip
              label={`Total Positions: ${positions.length}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`Filtered: ${filteredAndSorted.length}`}
              variant="outlined"
            />
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Sparkline Period:
              </Typography>
              <Chip
                label={sparklinePeriod}
                size="small"
                onClick={() => {
                  const periods: Array<'1H' | '12H' | '1D' | '7D' | '1M' | '1Y'> = ['1H', '12H', '1D', '7D', '1M', '1Y'];
                  const currentIndex = periods.indexOf(sparklinePeriod);
                  const nextIndex = (currentIndex + 1) % periods.length;
                  setSparklinePeriod(periods[nextIndex]);
                }}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* Core Identity */}
                  <TableCell
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('ticker')}
                  >
                    Ticker {sortBy === 'ticker' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Bucket</TableCell>
                  
                  {/* Cost vs Current */}
                  <TableCell
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('costBasis')}
                  >
                    Cost Basis (Price) {sortBy === 'costBasis' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cost Basis (Value)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Price</TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('value')}
                  >
                    Last Value {sortBy === 'value' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  
                  {/* Performance */}
                  <TableCell
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('dayChange')}
                  >
                    Day Change ($ / %) {sortBy === 'dayChange' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                    onClick={() => handleSort('gain')}
                  >
                    Total Gain ($ / %) {sortBy === 'gain' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableCell>
                  
                  {/* Trend Context */}
                  <TableCell sx={{ fontWeight: 'bold' }}>Trend ({sparklinePeriod})</TableCell>
                  
                  {/* System Intelligence */}
                  <TableCell sx={{ fontWeight: 'bold' }}>Ladder Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Recommendation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSorted.map((position) => {
                  const recommendation = getRecommendation(position.ticker);
                  const costBasisPrice = getCostBasisPrice(position);
                  const totalGainDollar = getTotalGainDollar(position);
                  const dayChange = position.dayChange;
                  
                  return (
                    <>
                      <TableRow key={position.ticker} hover>
                        {/* Core Identity */}
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {position.ticker}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={position.bucket || 'N/A'} size="small" />
                        </TableCell>
                        
                        {/* Cost vs Current */}
                        <TableCell>{formatCurrency(costBasisPrice)}</TableCell>
                        <TableCell>{formatCurrency(position.costBasis)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(position.currentPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(position.currentValue)}
                          </Typography>
                        </TableCell>
                        
                        {/* Performance */}
                        <TableCell>
                          {dayChange && (dayChange.dollar !== null && dayChange.dollar !== undefined) && (dayChange.percent !== null && dayChange.percent !== undefined) ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {dayChange.dollar >= 0 ? (
                                <TrendingUp color="success" fontSize="small" />
                              ) : (
                                <TrendingDown color="error" fontSize="small" />
                              )}
                              <Typography
                                variant="body2"
                                color={dayChange.dollar >= 0 ? 'success.main' : 'error.main'}
                                fontWeight="medium"
                              >
                                {formatDayChange(position)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {position.gainPct >= 0 ? (
                              <TrendingUp color="success" fontSize="small" />
                            ) : (
                              <TrendingDown color="error" fontSize="small" />
                            )}
                            <Typography
                              variant="body2"
                              color={position.gainPct >= 0 ? 'success.main' : 'error.main'}
                              fontWeight="medium"
                            >
                              {formatCurrency(totalGainDollar)} ({formatPercent(position.gainPct)})
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        {/* Trend Context */}
                        <TableCell>
                          <SparklineWithPeriod
                            ticker={position.ticker}
                            defaultPeriod={sparklinePeriod}
                            height={40}
                            width={120}
                            showPeriodToggle={false}
                            purchaseDate={position.purchaseDate || position.purchase_date}
                          />
                        </TableCell>
                        
                        {/* System Intelligence */}
                        <TableCell>
                          <Chip
                            label={position.lastLadderStep || 'NONE'}
                            size="small"
                            color={
                              !position.lastLadderStep || position.lastLadderStep === 'NONE'
                                ? 'default'
                                : position.lastLadderStep.includes('STEP_100')
                                ? 'success'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {recommendation ? (
                            <Tooltip title={recommendation.reasonCode} arrow>
                              <Chip
                                label={recommendation.actionType}
                                size="small"
                                color={
                                  recommendation.actionType === 'INCREASE_POSITION'
                                    ? 'success'
                                    : recommendation.actionType === 'TRIM_POSITION'
                                    ? 'error'
                                    : 'default'
                                }
                                icon={
                                  recommendation.actionType === 'INCREASE_POSITION' ? (
                                    <CheckCircle fontSize="small" />
                                  ) : recommendation.actionType === 'TRIM_POSITION' ? (
                                    <Warning fontSize="small" />
                                  ) : undefined
                                }
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {position.priceTimestamp && (
                      <TableRow sx={{ '& td': { borderTop: 'none', borderBottom: 'none', py: 0 } }}>
                        <TableCell colSpan={4} sx={{ border: 'none', py: 0, height: 0 }}></TableCell>
                        <TableCell colSpan={2} sx={{ border: 'none', py: 0, px: 1 }}>
                          <Tooltip title={formatLastRunTimeFull(position.priceTimestamp)} arrow>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ cursor: 'help', textAlign: 'center', display: 'block' }}
                            >
                              as of {formatLastRunTime(position.priceTimestamp)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell colSpan={5} sx={{ border: 'none', py: 0, height: 0 }}></TableCell>
                      </TableRow>
                    )}
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
